import {
	ContentType,
	validateYouTubeMedia,
	YOUTUBE_MAX_VIDEO_BYTES,
	YOUTUBE_VIDEO_EXTENSIONS,
} from "shared";
import { readVideoMetadataFromFile } from "../post/readVideoMetadata";

const MAX_MB = Math.round(YOUTUBE_MAX_VIDEO_BYTES / (1024 * 1024));

/**
 * Client-side Shorts checks that must pass before staging upload starts.
 * Format/size first, then duration and 9:16 from local metadata.
 */
export const validateYouTubeShortsFileBeforeUpload = async (
	file: File,
): Promise<{ valid: boolean; errors: string[] }> => {
	const extension = file.name.split(".").pop()?.toLowerCase();
	const formatCheck = validateYouTubeMedia([
		{
			extension,
			size: file.size,
			type: ContentType.VIDEO,
			filename: file.name,
		},
	]);
	if (!formatCheck.valid) {
		return { valid: false, errors: formatCheck.errors };
	}

	if (
		!extension ||
		!YOUTUBE_VIDEO_EXTENSIONS.includes(
			extension as (typeof YOUTUBE_VIDEO_EXTENSIONS)[number],
		)
	) {
		return {
			valid: false,
			errors: [
				`YouTube Shorts video must be MP4 or MOV (got .${extension ?? "unknown"}).`,
			],
		};
	}

	if (file.size > YOUTUBE_MAX_VIDEO_BYTES) {
		return {
			valid: false,
			errors: [`YouTube Shorts video must be ${MAX_MB} MB or smaller.`],
		};
	}

	try {
		const meta = await readVideoMetadataFromFile(file);
		const mediaCheck = validateYouTubeMedia([
			{
				extension,
				size: file.size,
				type: ContentType.VIDEO,
				filename: file.name,
				width: meta.width,
				height: meta.height,
				durationSec: meta.durationSec,
			},
		]);
		return { valid: mediaCheck.valid, errors: mediaCheck.errors };
	} catch {
		return {
			valid: false,
			errors: [
				"Could not read video duration or dimensions. Shorts must be vertical (9:16) and 60 seconds or less.",
			],
		};
	}
};
